import { getGatewayBaseUrl } from "../config";
import {
  calculateFinalPayableRounding,
  finalPayableClientSnapshot,
  type FinalPayableRoundingResult,
} from "../commerce/final-payable-rounding";
import { resolveLocalSaleFinalPayable } from "../commerce/local-sale-final-payable";
import { PosCreditSaleGatewayClient } from "../gateway/credit-sale-client";
import type { CommerceCalculationRuntimeItem } from "../gateway/local-projection-client";
import type { PosSaleGatewayLine, PosSalePromotionClaimEvidence, PosSaleSyncResult } from "../gateway/pos-sale-client";
import type { CashShiftSnapshot, EmployeeRuntimeActor } from "../gateway/identity-client";
import { normalizePromotionClaimEvidence } from "../promotion/promotion-claim-session";
import { freshLocalSaleQuote } from "./cash-sale-state";
import type { LocalCartDraft, LocalSaleMember, LocalSaleQuote } from "./cart-state";
import {
  acknowledgeLocalCustomerBenefitUsage,
  writeLocalCustomerBenefitUsage,
} from "./customer-benefit-runtime";
import {
  clearLocalLoyaltyRedemption,
  readLocalLoyaltyRedemption,
  type LocalLoyaltyRedemption,
} from "./loyalty-redemption-state";
import { posRuntimeDb, type JournalRecord, type OutboxRecord } from "./pos-db";
import type { TrustedTerminalIdentity } from "./terminal-state";

export type LocalCreditSaleSnapshot = {
  schema: "LOCAL_CREDIT_SALE_V1";
  localTransactionId: string;
  rootRequestKey: string;
  cartLocalId: string;
  sourceCartRequestKey?: string;
  organizationId: string;
  branchId: string;
  nodeId: string;
  commerceChannelId: string;
  performingEmployeeId: string;
  responsibleShiftEmployeeId: string;
  shiftId: string;
  catalogRevision: number;
  lines: LocalCartDraft["lines"];
  selectedMember: LocalSaleMember;
  claimedPromotionVersionIds: string[];
  quote: LocalSaleQuote;
  loyaltyRedemption: LocalLoyaltyRedemption | null;
  finalPayable?: FinalPayableRoundingResult;
  localTotal: number;
  currencyCode: string;
  executionMode: "ONLINE" | "OFFLINE";
  localCommittedAt: string;
  canonical: PosSaleSyncResult | null;
};

type CreditSaleOutboxPayload = {
  localTransactionId: string;
  cartLocalId: string;
  sourceCartRequestKey?: string;
  lines: PosSaleGatewayLine[];
  catalogRevision: number;
  localTotal: number;
  localCommittedAt: string;
  executionMode: "ONLINE" | "OFFLINE";
  promotionClaimEvidence: PosSalePromotionClaimEvidence[];
  saleContext: Record<string, unknown>;
};

function randomId() {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function saleScope(identity: TrustedTerminalIdentity) {
  return `org:${identity.organization_id}:branch:${identity.branch_id}:node:${identity.node_id}`;
}

function saleLocalId(localTransactionId: string) {
  return `sale:${localTransactionId}`;
}

function customerClaimEvidence(value: PosSalePromotionClaimEvidence[]) {
  return value.filter((item): item is { claim_instance_id: string; customer_id: string } => "claim_instance_id" in item);
}

function snapshotFromJournal(record: JournalRecord) {
  const snapshot = record.snapshot as LocalCreditSaleSnapshot;
  return snapshot?.schema === "LOCAL_CREDIT_SALE_V1" ? snapshot : null;
}

async function pendingIntentForCart(identity: TrustedTerminalIdentity, sourceCartRequestKey: string) {
  const scopeKey = saleScope(identity);
  const rows = await posRuntimeDb.journal
    .where("scopeKey")
    .equals(scopeKey)
    .filter((record) => record.kind === "SALE_ORDER" && record.state !== "CONFIRMED")
    .toArray();
  return rows.find((record) => snapshotFromJournal(record)?.sourceCartRequestKey === sourceCartRequestKey) ?? null;
}

export async function readPendingLocalCreditExposure(identity: TrustedTerminalIdentity, customerId: string) {
  const scopeKey = saleScope(identity);
  const rows = await posRuntimeDb.journal
    .where("scopeKey")
    .equals(scopeKey)
    .filter((record) => record.kind === "SALE_ORDER" && !["CONFIRMED", "REJECTED"].includes(record.state))
    .toArray();

  const candidates = rows.flatMap((record) => {
    const snapshot = snapshotFromJournal(record);
    if (!snapshot || snapshot.canonical || snapshot.selectedMember.customerId !== customerId) return [];
    return [{ record, snapshot }];
  });
  const outboxes = await posRuntimeDb.outbox.bulkGet(candidates.map(({ record }) => record.requestKey));

  return candidates.reduce((exposure, { snapshot }, index) => {
    // ACKED means the Owner contract already accepted this request. Do not keep a
    // stale split-brain Journal row reserved against local credit availability.
    if (outboxes[index]?.state === "ACKED") return exposure;
    return exposure + snapshot.localTotal;
  }, 0);
}

export async function commitLocalCreditSaleIntent(input: {
  identity: TrustedTerminalIdentity;
  actor: EmployeeRuntimeActor;
  shift: CashShiftSnapshot;
  cart: LocalCartDraft;
  catalogRevision: number;
  commerceCalculation: CommerceCalculationRuntimeItem;
  onlineAtCommit: boolean;
  availableCredit: number | null;
  promotionClaimEvidence?: PosSalePromotionClaimEvidence[];
}) {
  if (!input.cart.lines.length) throw new Error("CREDIT_SALE_CART_EMPTY");
  if (!input.actor.permission_codes.includes("commerce.sale.commit")) throw new Error("COMMERCE_SALE_COMMIT_PERMISSION_REQUIRED");
  if (input.shift.shift_status !== "OPEN") throw new Error("ACTIVE_POS_SHIFT_REQUIRED");
  if (!input.cart.selectedMember) throw new Error("CREDIT_SALE_CUSTOMER_REQUIRED");
  if (input.cart.lines.some((line) => line.modifiers.some((modifier) => modifier.quantity !== 1))) {
    throw new Error("MODIFIER_QUANTITY_SYNC_NOT_SUPPORTED");
  }

  const existing = await pendingIntentForCart(input.identity, input.cart.requestKey);
  if (existing) {
    const snapshot = snapshotFromJournal(existing);
    if (!snapshot) throw new Error("CREDIT_SALE_PENDING_INTENT_INVALID");
    return snapshot;
  }

  const quote = freshLocalSaleQuote(input.cart);
  if (!quote) throw new Error("POS_SALE_QUOTE_REQUIRED");
  const currencyCode = quote.currencyCode ?? input.cart.lines[0]?.currencyCode ?? "THB";
  const redemption = await readLocalLoyaltyRedemption(input.identity, input.cart.requestKey);
  const baseFinalPayable = calculateFinalPayableRounding(quote.postPromotionTotal, input.commerceCalculation);
  const finalPayable = resolveLocalSaleFinalPayable(baseFinalPayable, redemption, input.cart, currencyCode);
  const localTotal = finalPayable.totalAmount;
  if (!Number.isFinite(localTotal) || localTotal <= 0) throw new Error("CREDIT_SALE_TOTAL_INVALID");
  if (input.availableCredit == null || localTotal > input.availableCredit) {
    throw new Error("CREDIT_SALE_NOT_ELIGIBLE:CREDIT_LIMIT_EXCEEDED");
  }

  const promotionClaimEvidence = normalizePromotionClaimEvidence(input.promotionClaimEvidence);
  const customerClaims = customerClaimEvidence(promotionClaimEvidence);
  if (customerClaims.some((item) => item.customer_id !== input.cart.selectedMember?.customerId)) {
    throw new Error("PROMOTION_CLAIM_CUSTOMER_MISMATCH");
  }
  if (customerClaims.length && !quote.appliedPromotions.some((promotion) => promotion.activationCode === "CLAIM_CODE")) {
    throw new Error("PROMOTION_CLAIM_QUOTE_VALIDATION_REQUIRED");
  }
  const hasUnsafeAppliedPromotion = quote.appliedPromotions.some(
    (promotion) => promotion.offlineCapability && promotion.offlineCapability !== "OFFLINE_SAFE",
  );
  if (!input.onlineAtCommit && promotionClaimEvidence.length) throw new Error("PROMOTION_CLAIM_REQUIRES_ONLINE");
  if (!input.onlineAtCommit && (quote.requiresOnlineResolution || hasUnsafeAppliedPromotion)) {
    throw new Error("PROMOTION_CONTEXT_REQUIRES_ONLINE");
  }

  const localTransactionId = randomId();
  const rootRequestKey = `pos-credit-sale:${randomId()}`;
  const localCommittedAt = new Date().toISOString();
  const scopeKey = saleScope(input.identity);
  const executionMode = input.onlineAtCommit ? "ONLINE" : "OFFLINE";
  const lines: PosSaleGatewayLine[] = input.cart.lines.map((line) => ({
    line_id: line.lineId,
    menu_item_id: line.menuItemId,
    menu_variant_id: line.menuVariantId,
    quantity: line.quantity,
    local_unit_price: line.unitPrice,
    price_version_id: line.priceVersionId,
    modifier_option_ids: line.modifiers.map((modifier) => modifier.modifierOptionId),
  }));

  const snapshot: LocalCreditSaleSnapshot = {
    schema: "LOCAL_CREDIT_SALE_V1",
    localTransactionId,
    rootRequestKey,
    cartLocalId: input.cart.localCartId,
    sourceCartRequestKey: input.cart.requestKey,
    organizationId: input.identity.organization_id,
    branchId: input.identity.branch_id,
    nodeId: input.identity.node_id,
    commerceChannelId: input.identity.commerce_channel_id,
    performingEmployeeId: input.actor.employee_id,
    responsibleShiftEmployeeId: input.shift.responsible_employee_id,
    shiftId: input.shift.shift_id,
    catalogRevision: input.catalogRevision,
    lines: input.cart.lines,
    selectedMember: input.cart.selectedMember,
    claimedPromotionVersionIds: [...input.cart.claimedPromotionVersionIds],
    quote,
    loyaltyRedemption: redemption,
    finalPayable,
    localTotal,
    currencyCode,
    executionMode,
    localCommittedAt,
    canonical: null,
  };

  const payload: CreditSaleOutboxPayload = {
    localTransactionId,
    cartLocalId: input.cart.localCartId,
    sourceCartRequestKey: input.cart.requestKey,
    lines,
    catalogRevision: input.catalogRevision,
    localTotal,
    localCommittedAt,
    executionMode,
    promotionClaimEvidence,
    saleContext: {
      customer_id: input.cart.selectedMember.customerId,
      membership_id: input.cart.selectedMember.membershipId,
      claimed_promotion_version_ids: input.cart.claimedPromotionVersionIds,
      loyalty_reward_redemption_id: redemption?.redemptionId ?? null,
      quote_fingerprint: quote.quoteFingerprint ?? null,
      quote_evaluated_at: quote.evaluatedAt ?? null,
      promotion_runtime_revision: quote.promotionRuntimeRevision ?? null,
      promotion_quote_source: quote.quoteSource ?? null,
      applied_promotion_version_ids: quote.appliedPromotions.map((promotion) => promotion.promotionVersionId),
      final_payable: finalPayableClientSnapshot(finalPayable),
    },
  };

  const outbox: OutboxRecord = {
    requestKey: rootRequestKey,
    scopeKey,
    operation: "SYNC_COMMERCE_POS_CREDIT_SALE_V1",
    payload,
    state: "PENDING",
    attempts: 0,
    nextAttemptAt: null,
    lastError: null,
    createdAt: localCommittedAt,
    updatedAt: localCommittedAt,
  };

  await posRuntimeDb.transaction("rw", posRuntimeDb.journal, posRuntimeDb.outbox, async () => {
    await posRuntimeDb.journal.put({
      localId: saleLocalId(localTransactionId),
      scopeKey,
      kind: "SALE_ORDER",
      state: "PENDING_SERVER_CONFIRMATION",
      requestKey: rootRequestKey,
      snapshot,
      createdAt: localCommittedAt,
      updatedAt: localCommittedAt,
    });
    if (redemption) {
      await writeLocalCustomerBenefitUsage(
        input.identity,
        { source_type: redemption.sourceType, source_instance_id: redemption.sourceInstanceId, customer_id: redemption.customerId },
        rootRequestKey,
        localTransactionId,
        localCommittedAt,
      );
    }
    for (const claim of customerClaims) {
      await writeLocalCustomerBenefitUsage(
        input.identity,
        { source_type: "PROMOTION_CLAIM", source_instance_id: claim.claim_instance_id, customer_id: claim.customer_id },
        rootRequestKey,
        localTransactionId,
        localCommittedAt,
      );
    }
    await posRuntimeDb.outbox.put(outbox);
    await posRuntimeDb.journal.delete(input.cart.localCartId);
  });
  return snapshot;
}

async function markOutboxFailure(record: OutboxRecord, message: string) {
  const now = new Date().toISOString();
  await posRuntimeDb.outbox.put({
    ...record,
    state: "FAILED",
    attempts: record.attempts + 1,
    nextAttemptAt: new Date(Date.now() + 5_000).toISOString(),
    lastError: message,
    updatedAt: now,
  });
}

const frontlineSafeCreditErrorMarkers = [
  "CREDIT_SALE_REQUIRES_ONLINE",
  "CREDIT_SALE_CUSTOMER_REQUIRED",
  "CREDIT_SALE_NOT_ELIGIBLE:",
  "ACTIVE_POS_SHIFT_REQUIRED",
  "COMMERCE_SALE_COMMIT_PERMISSION_REQUIRED",
  "LOCAL_TOTAL_MISMATCH",
  "LOCAL_PRICE_AMOUNT_MISMATCH",
  "PRICE_VERSION_MISMATCH",
  "PROMOTION_CLAIM_",
  "LOYALTY_REWARD_",
  "PUBLISHED_MODIFIER_CONSUMPTION_EFFECT_RUNTIME_REQUIRED",
  "MODIFIER_QUANTITY_SYNC_NOT_SUPPORTED",
  "POS_EMPLOYEE_RUNTIME_SESSION_INVALID",
  "GATEWAY_UNREACHABLE",
  "GATEWAY_TIMEOUT",
];

function frontlineCreditError(rawError: string) {
  if (frontlineSafeCreditErrorMarkers.some((marker) => rawError.includes(marker))) return rawError;
  return "ยังยืนยันรายการค้างชำระไม่สำเร็จ ระบบเก็บคำขอเดิมไว้เพื่อ Retry กรุณาลองอีกครั้ง";
}

export async function syncCreditSaleOutbox(
  identity: TrustedTerminalIdentity,
  deviceSecret: string,
  sessionToken: string,
  requestKey: string,
) {
  const record = await posRuntimeDb.outbox.get(requestKey);
  if (!record || record.operation !== "SYNC_COMMERCE_POS_CREDIT_SALE_V1") throw new Error("CREDIT_SALE_OUTBOX_NOT_FOUND");
  const saleRecord = await posRuntimeDb.journal.where("requestKey").equals(requestKey).first();
  if (!saleRecord || saleRecord.kind !== "SALE_ORDER") throw new Error("LOCAL_SALE_JOURNAL_NOT_FOUND");
  const saleSnapshot = snapshotFromJournal(saleRecord);
  if (!saleSnapshot) throw new Error("LOCAL_CREDIT_SALE_SNAPSHOT_INVALID");
  if (record.state === "ACKED" && saleSnapshot.canonical) return saleSnapshot;

  const baseUrl = getGatewayBaseUrl();
  if (!baseUrl) {
    const rawError = "POS_GATEWAY_NOT_CONFIGURED";
    await markOutboxFailure(record, rawError);
    throw new Error(frontlineCreditError(rawError));
  }

  const payload = record.payload as CreditSaleOutboxPayload;
  await posRuntimeDb.outbox.put({ ...record, state: "SENDING", updatedAt: new Date().toISOString() });
  const client = new PosCreditSaleGatewayClient({ baseUrl });
  const result = await client.sync({
    deviceSecret,
    sessionToken,
    localTransactionId: payload.localTransactionId,
    lines: payload.lines,
    catalogRevision: payload.catalogRevision,
    localTotal: payload.localTotal,
    localCommittedAt: payload.localCommittedAt,
    executionMode: payload.executionMode ?? saleSnapshot.executionMode ?? "ONLINE",
    requestKey,
    promotionClaimEvidence: payload.promotionClaimEvidence,
    clientContext: {
      runtime: "WEBPOS",
      terminal_node_id: identity.node_id,
      local_transaction_id: payload.localTransactionId,
      local_commit_authority: true,
      settlement_kind: "RECEIVABLE",
      sale_context: payload.saleContext,
    },
  });

  if (!result.ok || !result.data) {
    const rawError = result.error ?? "COMMERCE_POS_CREDIT_SALE_SYNC_FAILED";
    await markOutboxFailure(record, rawError);
    throw new Error(frontlineCreditError(rawError));
  }

  const ackedAt = new Date().toISOString();
  const customerClaims = customerClaimEvidence(payload.promotionClaimEvidence);
  const sanitizedPayload: CreditSaleOutboxPayload = { ...payload, promotionClaimEvidence: [] };
  const confirmedSnapshot: LocalCreditSaleSnapshot = { ...saleSnapshot, canonical: result.data };
  await posRuntimeDb.transaction("rw", posRuntimeDb.journal, posRuntimeDb.outbox, async () => {
    await posRuntimeDb.journal.put({ ...saleRecord, state: "CONFIRMED", snapshot: confirmedSnapshot, updatedAt: ackedAt });
    await posRuntimeDb.outbox.put({
      ...record,
      payload: sanitizedPayload,
      state: "ACKED",
      attempts: record.attempts + 1,
      nextAttemptAt: null,
      lastError: null,
      updatedAt: ackedAt,
    });
  });
  if (saleSnapshot.loyaltyRedemption) {
    await acknowledgeLocalCustomerBenefitUsage(
      identity,
      saleSnapshot.loyaltyRedemption.sourceType,
      saleSnapshot.loyaltyRedemption.sourceInstanceId,
    );
    await clearLocalLoyaltyRedemption(identity, saleSnapshot.sourceCartRequestKey ?? saleSnapshot.cartLocalId);
  }
  for (const claim of customerClaims) {
    await acknowledgeLocalCustomerBenefitUsage(identity, "PROMOTION_CLAIM", claim.claim_instance_id);
  }
  return confirmedSnapshot;
}

export async function syncPendingCreditSales(identity: TrustedTerminalIdentity, deviceSecret: string, sessionToken: string) {
  const pending = await posRuntimeDb.outbox
    .where("operation")
    .equals("SYNC_COMMERCE_POS_CREDIT_SALE_V1")
    .filter((record) => record.state !== "ACKED")
    .sortBy("createdAt");
  const results: Array<{ requestKey: string; ok: boolean; error?: string }> = [];
  for (const record of pending) {
    if (record.scopeKey !== saleScope(identity)) continue;
    if (record.nextAttemptAt && Date.parse(record.nextAttemptAt) > Date.now()) continue;
    try {
      await syncCreditSaleOutbox(identity, deviceSecret, sessionToken, record.requestKey);
      results.push({ requestKey: record.requestKey, ok: true });
    } catch (error) {
      results.push({ requestKey: record.requestKey, ok: false, error: error instanceof Error ? error.message : "COMMERCE_POS_CREDIT_SALE_SYNC_FAILED" });
    }
  }
  return results;
}
